/**
 * The visible-tab heartbeat (Phase 2 contract).
 *
 * While the document is visible, `tick(tickMs)` fires every `tickMs`. When
 * the tab is hidden the ticker stops; when it becomes visible again the
 * elapsed window is first caught up via `catchUp()` (the offline-catchup
 * path — complete intervals only) and only then does the ticker restart, so
 * background time is neither dropped nor double-counted. Repeated
 * visibility flips never overlap catch-ups.
 *
 * Extracted from App.tsx so the timing behavior is testable with a fake
 * document and fake timers.
 */

/** The slice of Document the heartbeat needs — injectable for tests. */
export type HeartbeatDoc = Pick<
  Document,
  'visibilityState' | 'addEventListener' | 'removeEventListener'
>;

export type HeartbeatOptions = {
  tickMs: number;
  tick: (dtMs: number) => void;
  /** Applies the elapsed offline window; must be safe to call repeatedly. */
  catchUp: () => Promise<void>;
  doc?: HeartbeatDoc;
};

/** Starts the heartbeat; returns a cleanup function. */
export function startHeartbeat(options: HeartbeatOptions): () => void {
  const doc: HeartbeatDoc = options.doc ?? document;
  let interval: ReturnType<typeof setInterval> | null = null;
  let catchingUp = false;
  let disposed = false;

  function startTicking() {
    if (interval !== null || disposed) return;
    interval = setInterval(() => options.tick(options.tickMs), options.tickMs);
  }

  function stopTicking() {
    if (interval !== null) {
      clearInterval(interval);
      interval = null;
    }
  }

  function onVisibilityChange() {
    if (doc.visibilityState !== 'visible') {
      stopTicking();
      return;
    }
    if (catchingUp) return; // the in-flight catch-up will restart the ticker
    catchingUp = true;
    stopTicking();
    void options
      .catchUp()
      .catch(() => undefined)
      .finally(() => {
        catchingUp = false;
        if (!disposed && doc.visibilityState === 'visible') startTicking();
      });
  }

  if (doc.visibilityState === 'visible') startTicking();
  doc.addEventListener('visibilitychange', onVisibilityChange);
  return () => {
    disposed = true;
    stopTicking();
    doc.removeEventListener('visibilitychange', onVisibilityChange);
  };
}
